import type { DeleteTestResultsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (
  numIds: number
): DeleteTestResultsMutation => ({
  delete_relation_file: {
    __typename: 'relation_file_mutation_response',
    affected_rows: numIds,
  },
  delete_test_result: {
    __typename: 'test_result_mutation_response',
    affected_rows: numIds,
  },
});

export const useDeleteTestResultsTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.testResult.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResultById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResultsByControlId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.testResult.latestTestResultsByControlId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResults.queryKey(),
        });
        // COI-471 parity: Apollo twin also evicts `control` and `risk_score`
        // (test results feed control effectiveness / risk scores).
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.control.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    deleteTestResults: async (variables: {
      ids: string[];
    }): Promise<DeleteTestResultsMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(variables.ids.length);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
